import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, EmptyState, PageHeader, Skeleton, TableSkeleton } from "../components/ui";
import { downloadBlob } from "../lib/download";
import { formatDate, formatMoney } from "../lib/format";
import { ATTENDANCE_LABEL, PAYROLL_LABEL, PAYROLL_TONE, formatMinutes } from "../lib/people";
import { payslipFilename } from "../lib/payslip";
import type { PayrollAttendanceDay, PayrollItem } from "../api/types";

/**
 * My payslips.
 *
 * The payslip endpoint already scopes a non-admin to their own approved, paid
 * or locked payslips, so this screen never filters for privacy -- it simply
 * shows what the API returns for the signed-in login.
 */
export function MyPayslipsPage() {
  const payslips = useQuery({
    queryKey: ["my-payslips"],
    queryFn: ({ signal }) => api.payslips({ page_size: 24 }, signal),
  });

  return (
    <>
      <PageHeader
        title="My payslips"
        subtitle="Your payslips and the attendance behind each period."
      />

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {payslips.isPending ? (
          <TableSkeleton rows={5} />
        ) : payslips.isError ? (
          <EmptyState
            title="Could not load payslips"
            body={payslips.error instanceof ApiError ? payslips.error.message : "Please try again."}
          />
        ) : (payslips.data?.results ?? []).length === 0 ? (
          <EmptyState
            title="No payslips yet"
            body="A payslip appears once your payroll run has been approved."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {(payslips.data?.results ?? []).map((item: PayrollItem) => (
              <PayslipRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function PayslipRow({ item }: { item: PayrollItem }) {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const breakdown = useQuery({
    queryKey: ["my-payslip-attendance", item.id],
    queryFn: ({ signal }) => api.payrollItemAttendance(item.id, signal),
    enabled: open,
  });

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await api.payslipDownload(item.id);
      downloadBlob(blob, payslipFilename(item));
    } catch (error) {
      setDownloadError(
        error instanceof ApiError ? error.message : "Could not prepare the payslip.",
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <li>
      <div className="flex items-center gap-2 px-4 py-3 hover:bg-slate-50">
        <button
          onClick={() => setOpen((value) => !value)}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
          aria-expanded={open}
        >
          <span className="flex items-center gap-2">
            {open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
            <span>
              <span className="block text-sm font-medium text-slate-900">{item.run_number}</span>
              <span className="block text-xs text-slate-500">
                {formatDate(item.period_start, timezone ?? undefined)} &rarr;{" "}
                {formatDate(item.period_end, timezone ?? undefined)}
              </span>
            </span>
          </span>
          <span className="flex items-center gap-4">
            <span className="text-right">
              <span className="block text-sm font-semibold tabular-nums text-slate-900">
                {formatMoney(item.net_pay)}
              </span>
              <span className="block text-xs text-slate-400">Net pay</span>
            </span>
            <Badge tone={PAYROLL_TONE[item.status ?? "draft"]}>
              {PAYROLL_LABEL[item.status ?? "draft"]}
            </Badge>
          </span>
        </button>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          aria-label={`Download payslip PDF ${item.run_number}`}
          title="Download payslip PDF"
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 hover:text-slate-900 disabled:opacity-50"
        >
          <Download size={13} /> {downloading ? "Preparing" : "Download PDF"}
        </button>
      </div>
      {downloadError ? (
        <p role="alert" className="px-4 pb-2 text-xs text-rose-600">
          {downloadError}
        </p>
      ) : null}


      {open ? (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
          {breakdown.isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : breakdown.isError ? (
            <p className="text-sm text-rose-600">Could not load the attendance for this period.</p>
          ) : breakdown.data ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
                <span>Present: <strong className="text-slate-900">{breakdown.data.summary.present}</strong></span>
                <span>Half day: <strong className="text-slate-900">{breakdown.data.summary.half_day}</strong></span>
                <span>Paid leave: <strong className="text-slate-900">{breakdown.data.summary.paid_leave}</strong></span>
                <span>Absent: <strong className="text-slate-900">{breakdown.data.summary.absent}</strong></span>
                <span>Worked: <strong className="text-slate-900">{formatMinutes(breakdown.data.summary.worked_minutes)}</strong></span>
                <span>Exceptions: <strong className="text-slate-900">{breakdown.data.summary.exceptions}</strong></span>
              </div>
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                <ul className="divide-y divide-slate-100">
                  {breakdown.data.days.map((day: PayrollAttendanceDay) => (
                    <li key={day.date} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="tabular-nums text-slate-600">{formatDate(day.date, timezone ?? undefined)}</span>
                      <span className="text-slate-500">{ATTENDANCE_LABEL[(day.status as never) ?? "present"] ?? day.status ?? "—"}</span>
                      <span className="text-slate-400">{day.exception ?? ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
